import { useSlideover } from "@/app/hooks/ui_hooks/useSlideover";
import { useSlideoverActions } from "@/app/hooks/ui_hooks/useSlideoverActions";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Fragment, PropsWithChildren, useEffect } from "react";
import BackdropComponent from "../../Backdrop";
import { ISlideoverWithStickyFooterProps } from "../interface";

const SlideoverWithStickyFooter: React.FC<PropsWithChildren<ISlideoverWithStickyFooterProps>> = ({
  buttons,
  slideoverId,
  onVisibleCallback,
  shouldCloseOnClick,
  slideOverWidth,
  ...props
}) => {
  const { setInvisible } = useSlideoverActions(slideoverId);
  const isShowing = useSlideover(slideoverId);

  const handleClose = () => {
    if (shouldCloseOnClick === false) {
      return;
    }
    setInvisible();
  };

  const handleSecondaryButtonClick = () => {
    if (buttons?.primaryButtonCallback) {
      buttons?.primaryButtonCallback();
    }
    if (shouldCloseOnClick) {
      setInvisible();
    }
  };
  const handlePrimaryButtonClick = () => {
    if (buttons?.primaryButtonCallback) {
      buttons?.primaryButtonCallback();
    }
    if (shouldCloseOnClick) {
      setInvisible();
    }
  };

  useEffect(() => {
    if (isShowing) {
      if (onVisibleCallback) {
        onVisibleCallback();
      }
    }
  }, [isShowing]);

  return (
    <Transition.Root show={isShowing} as={Fragment}>
      <Dialog as="div" className="relative z-40" onClose={handleClose}>
        <BackdropComponent blur={false} />
        <div className="fixed inset-0" />

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className={`pointer-events-auto w-screen max-w-${slideOverWidth || "md"}`}>
                  <div className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl">
                    <div className="flex min-h-0 flex-1 flex-col overflow-y-scroll py-6">
                      <div className="px-4 sm:px-6">
                        <div className="flex items-start justify-between">
                          <Dialog.Title>{props?.title ?? "Title"}</Dialog.Title>
                          <div className="ml-3 flex h-7 items-center">
                            <button
                              type="button"
                              className="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              onClick={setInvisible}
                            >
                              <span className="absolute -inset-2.5" />
                              <span className="sr-only">Close panel</span>
                              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="relative mt-6 flex-1 px-4 sm:px-6">{props?.children}</div>
                    </div>
                    {buttons && (
                      <div className="flex flex-shrink-0 justify-end px-4 py-4">
                        <button
                          type="button"
                          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:ring-gray-400"
                          onClick={handleSecondaryButtonClick}
                        >
                          {buttons?.secondaryButtonText ?? "Cancel"}
                        </button>
                        <button
                          type="button"
                          onClick={handlePrimaryButtonClick}
                          className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
                        >
                          {buttons?.primaryButtonText && "Ok"}
                        </button>
                      </div>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default SlideoverWithStickyFooter;
